import {
  MarchGiveawayTicketType,
  useMarchGiveawayStats,
  useMarchGiveawayTickets,
} from "@/entities/worker";
import { useAuthUser, useURLParamsData } from "@/shared/hooks";
import { cn } from "@/shared/lib/utils";
import { Label } from "@/shared/ui/label";
import { Progress } from "@/shared/ui/progress";
import { Skeleton } from "@/shared/ui/skeleton";

const GIVEAWAY_START = new Date("2026-03-09T18:00:00+03:00").getTime();
const GIVEAWAY_END = new Date("2026-04-09T18:00:00+03:00").getTime();

const isWithinGiveawayPeriod = (serverDate: string | undefined) => {
  if (!serverDate) return true;
  const time = new Date(serverDate).getTime();
  return time >= GIVEAWAY_START && time <= GIVEAWAY_END;
};

export const MarchGiveaway = () => {
  const user = useAuthUser();
  const { page, length } = useURLParamsData();

  const { data: tickets } = useMarchGiveawayTickets({
    workerIds: [user._id],
    currentPage: page,
    pageSize: length,
  });
  const { data: stats } = useMarchGiveawayStats({
    workerIds: [user._id],
    currentPage: page,
    pageSize: length,
  });

  const userStats = stats?.data[0];
  const serverDate = tickets?.serverDate ?? stats?.serverDate;

  if (!isWithinGiveawayPeriod(serverDate)) {
    return null;
  }

  const hasSilverTicket = tickets?.data.some(
    (ticket) => ticket.type === MarchGiveawayTicketType.Silver,
  );
  const hasDiamondTicket = tickets?.data.some(
    (ticket) => ticket.type === MarchGiveawayTicketType.Diamond,
  );

  const noTickets = !hasSilverTicket;

  return (
    <div className="flex flex-col">
      <Label className="text-sm font-normal md:text-[12px]">
        Конкурс (билеты)
      </Label>

      {!tickets || !stats ? (
        <Skeleton className="h-9 w-full rounded-xl" />
      ) : (
        <>
          <div className="relative">
            <Progress
              value={
                noTickets
                  ? ((userStats?.leadsCreatedCount ?? 0) / 50) * 100
                  : ((userStats?.leadsCreatedCount ?? 0) / 500) * 100
              }
              className={cn(
                "h-9 rounded-xl border border-[#E7E7EA]",
                noTickets
                  ? "bg-transparent"
                  : "progress-shadow bg-gradient-to-r from-[#696969] via-[#373737] to-[#030303] dark:bg-gradient-to-r",
              )}
              indicatorClassName={cn(
                "bg-gradient-to-r dark:bg-gradient-to-r rounded-xl progress-shadow",
                noTickets
                  ? "from-[#696969] via-[#373737] to-[#030303]"
                  : "from-[#309BFF] to-[#1CFFF4]",
              )}
            />
            <p
              className={cn(
                "absolute left-1/2 top-1/2 inline-flex -translate-x-1/2 -translate-y-1/2 gap-1 text-xs font-bold",
                noTickets
                  ? "[&>*]:text-thColor"
                  : "[&>*]:text-textInvertedColor",
              )}
            >
              <span>{userStats?.leadsCreatedCount ?? 0}</span>
              <span>/</span>
              <span>{noTickets ? "50" : "500"}</span>
              <span>Лидов</span>
            </p>
          </div>
          <div className="flex flex-col items-center gap-1 pt-1 text-[10px] font-semibold leading-3">
            {noTickets && <p className="text-thColor">Получено 0 билетов</p>}
            {hasSilverTicket && <p>Получен Серебряный билет</p>}
            {hasDiamondTicket && (
              <p className="bg-gradient-to-r from-[#309BFF] to-[#1CFFF4] bg-clip-text text-transparent">
                Получен Бриллиантовый билет
              </p>
            )}
          </div>
        </>
      )}
    </div>
  );
};
